import { useEffect, useRef } from 'react';

/**
 * Autosave guarantees (crash recovery, PLAN M14): debounced editors must never lose the last few
 * keystrokes when a tab closes (unmount), the window reloads or the app quits.
 *
 *  - `useFlushOnExit(flush)` runs `flush` on `pagehide` / `beforeunload` (and when the page is
 *    hidden, e.g. the window is minimised). Requests made while the page unloads use
 *    `fetch(…, { keepalive: true })` (see `lib/api.ts`), so they complete after the page is gone.
 *  - `useDebouncedSave(save, delay)` is the whole pattern: `schedule(value)` debounces, and the
 *    pending value is flushed on unmount and on exit.
 */

let unloading = false;
const flushers = new Set<() => void>();

function flushAll() {
  for (const f of flushers) {
    try {
      f();
    } catch {
      /* one editor failing must not stop the others */
    }
  }
}

if (typeof window !== 'undefined') {
  const onExit = () => {
    unloading = true;
    flushAll();
  };
  window.addEventListener('beforeunload', onExit);
  window.addEventListener('pagehide', onExit);
  // A reload that gets cancelled leaves the page alive: reset the flag on the next tick.
  window.addEventListener('pageshow', () => {
    unloading = false;
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAll();
  });
}

/** True while the page is unloading (requests then use `keepalive`). */
export const isUnloading = () => unloading;

/** Register a flush callback for page exit. The latest `flush` is always the one called. */
export function useFlushOnExit(flush: () => unknown) {
  const ref = useRef(flush);
  ref.current = flush;
  useEffect(() => {
    const f = () => void ref.current();
    flushers.add(f);
    return () => {
      flushers.delete(f);
    };
  }, []);
}

/** Debounced save that flushes the pending value on unmount and on page exit. */
export function useDebouncedSave<T>(save: (value: T) => unknown, delay = 700) {
  const saveRef = useRef(save);
  saveRef.current = save;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ value: T } | null>(null);
  const api = useRef({
    schedule(value: T) {
      pending.current = { value };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => api.current.flush(), delay);
    },
    flush() {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      const p = pending.current;
      pending.current = null;
      if (p) void saveRef.current(p.value);
    },
    cancel() {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      pending.current = null;
    },
  });
  useFlushOnExit(() => api.current.flush());
  useEffect(() => () => api.current.flush(), []);
  return api.current;
}
